/**
 * CCMS SDK Configuration
 *
 * Centralizes SDK configuration for the CCMS authentication and services.
 * The CCMS backend uses an nginx API gateway with path-based routing to
 * multiple microservices.
 *
 * Base URL: https://ccms.actschurch.co.za
 *
 * Service Routes:
 * - /users/          → User Management (auth, roles, permissions)
 * - /tasks/          → Task Management
 * - /members/        → Member Management
 * - /meetings/       → Meeting Management
 * - /files/          → File Management
 * - /ws/presence/    → Presence WebSocket Hub
 * - ... and 15+ other services
 */

// Base URL for all CCMS services (REST APIs via proxy)
// Empty string means relative URLs (e.g., /api/users) which Nginx will proxy to the backend
// In development: Use 'http://localhost:5174' for the local proxy server
// In production: Use '' (empty string) for relative URLs proxied by Nginx
export const CCMS_BASE_URL = import.meta.env.VITE_CCMS_BASE_URL !== undefined
  ? import.meta.env.VITE_CCMS_BASE_URL
  : 'http://localhost:5174';

// WebSocket base URL
// In development: Use the proxy server (http://localhost:5174)
// In production: Use empty string for relative URLs
export const CCMS_WS_BASE_URL = import.meta.env.VITE_CCMS_WS_BASE_URL !== undefined
  ? import.meta.env.VITE_CCMS_WS_BASE_URL
  : CCMS_BASE_URL;

// Debug: Log the actual URLs being used
console.log('='.repeat(60));
console.log('[CCMS Config] Configuration Loaded:');
console.log('[CCMS Config] CCMS_BASE_URL:', CCMS_BASE_URL);
console.log('[CCMS Config] CCMS_WS_BASE_URL:', CCMS_WS_BASE_URL);
console.log('[CCMS Config] VITE_CCMS_BASE_URL env:', import.meta.env.VITE_CCMS_BASE_URL);
console.log('[CCMS Config] VITE_CCMS_WS_BASE_URL env:', import.meta.env.VITE_CCMS_WS_BASE_URL);
console.log('='.repeat(60));

// API endpoint prefixes
export const API_PREFIXES = {
  users: '/users',
  tasks: '/tasks',
  members: '/members',
  departments: '/departments',
  locations: '/locations',
  hr: '/hr',
  payments: '/payments',
  orders: '/orders',
  products: '/products',
  procurements: '/procurements',
  suppliers: '/suppliers',
  resources: '/resources',
  meetings: '/meetings',
  feedback: '/feedback',
  reports: '/reports',
  projects: '/projects',
  communications: '/communications',
  notifications: '/notifications',
  files: '/files',
  codeGenerators: '/code-generators',
  system: '/system',
} as const;

// WebSocket hubs
// For presence/chats: full hub paths (single hub per service)
// For logs/dashboards: base paths (multiple hubs per service, SDK appends specific paths)
export const WS_HUBS = {
  presence: '/ws/presence/presencehub',
  chats: '/ws/chats/chathub',
  dashboards: '/ws/dashboards',
  logs: '/ws/logs',
} as const;

// Auth configuration
export const AUTH_CONFIG = {
  autoRefresh: true,
  refreshThresholdSeconds: 300, // Refresh 5 minutes before token expires
  refreshCheckIntervalMs: 30000, // Check every 30 seconds
} as const;

// Presence configuration
export const PRESENCE_CONFIG = {
  activityTimeout: 5 * 60 * 1000, // 5 minutes of inactivity before marked as "Away"
  enableActivityDetection: true,
  platform: 'web' as const,
  autoReconnect: true,
  maxReconnectAttempts: 10,
  logLevel: 6, // 0=None, 1=Critical, 2=Error, 3=Warning, 4=Information, 5=Debug, 6=Trace (use 6 for debugging)

  // PresenceManager automatic features configuration
  autoHeartbeat: true,
  heartbeatInterval: 30, // seconds
  activityTracking: true,
  activityDebounce: 5000, // milliseconds - DEPRECATED, kept for backward compatibility
  highSignificanceThrottle: 2000, // milliseconds - Throttle for keyboard/clicks (2 seconds)
  lowSignificanceThrottle: 5000, // milliseconds - Throttle for mouse/scroll (5 seconds)
  idleDetection: true,
  idleTimeout: 5, // minutes
  idleGracePeriod: 30, // seconds - Grace period before marking idle (30 seconds)
  autoLockOnIdle: true, // Lock screen when user becomes idle
};

// Storage keys
export const STORAGE_KEYS = {
  // SDK uses this key internally for auth data
  auth: 'ccms_auth_user',
  // Legacy key for migration
  legacyAuth: 'ccms_auth',
  preferences: 'ccms_preferences',
} as const;

// Default domains (always include demo)
export const DEFAULT_DOMAINS = ['demo'] as const;

// API endpoint helpers
// NOTE: These return PATHS only (not full URLs) because the SDK's HttpClient
// already has baseUrl configured and will prepend it automatically
export const getApiUrl = (prefix: keyof typeof API_PREFIXES, path: string = '') => {
  return `${API_PREFIXES[prefix]}${path}`;
};

export const getWsUrl = (hub: keyof typeof WS_HUBS) => {
  return `${CCMS_WS_BASE_URL}${WS_HUBS[hub]}`;
};
